import type { MachineOutputPeriod, MachineOutputUnit, MachineRecord, MachineType } from '../types/records'

export const MACHINE_TYPES: {
  value: MachineType
  label: string
  accent: string
}[] = [
  { value: 'toilet_paper', label: 'Туалетная бумага', accent: '#2563eb' },
  { value: 'napkins', label: 'Салфетки', accent: '#7c3aed' },
  { value: 'paper_towels', label: 'Бумажные полотенца', accent: '#0d9488' },
]

export const MACHINE_OUTPUT_UNITS: { value: MachineOutputUnit; label: string; short: string }[] = [
  { value: 'sheets', label: 'Листы', short: 'лист' },
  { value: 'rolls', label: 'Рулоны', short: 'рул' },
]

export const MACHINE_OUTPUT_PERIODS: { value: MachineOutputPeriod; label: string; short: string }[] = [
  { value: 'minute', label: 'За минуту', short: 'мин' },
  { value: 'hour', label: 'За час', short: 'час' },
  { value: 'shift', label: 'За смену', short: 'смена' },
]

const TYPE_BY_VALUE = Object.fromEntries(MACHINE_TYPES.map((t) => [t.value, t])) as Record<
  MachineType,
  (typeof MACHINE_TYPES)[number]
>

const UNIT_SHORT = Object.fromEntries(MACHINE_OUTPUT_UNITS.map((u) => [u.value, u.short])) as Record<
  MachineOutputUnit,
  string
>

const PERIOD_SHORT = Object.fromEntries(MACHINE_OUTPUT_PERIODS.map((p) => [p.value, p.short])) as Record<
  MachineOutputPeriod,
  string
>

export function machineTypeLabel(type: MachineType): string {
  return TYPE_BY_VALUE[type]?.label ?? type
}

/** Полное название станка для списков и форм: «Бумажные полотенца №1». */
export function formatMachineLabel(m: Pick<MachineRecord, 'type' | 'number'>): string {
  return `${machineTypeLabel(m.type)} №${m.number}`
}

export function machineTypeAccent(type: MachineType): string {
  return TYPE_BY_VALUE[type]?.accent ?? '#2563eb'
}

export function formatMachineOutput(m: Pick<MachineRecord, 'output_quantity' | 'output_unit' | 'output_period'>): string {
  const qty = m.output_quantity
  const unit = UNIT_SHORT[m.output_unit] ?? m.output_unit
  const period = PERIOD_SHORT[m.output_period] ?? m.output_period
  return `${qty} ${unit}/${period}`
}

export function availabilityPercent(
  m: Pick<MachineRecord, 'working_hours_per_day' | 'maintenance_hours_per_day'>,
): number {
  const working = m.working_hours_per_day
  if (working <= 0) return 0
  const available = Math.max(0, working - m.maintenance_hours_per_day)
  return Math.min(100, Math.max(0, Math.round((available / working) * 100)))
}

export function groupMachinesByType(
  machines: MachineRecord[],
): { type: MachineType; label: string; accent: string; items: MachineRecord[] }[] {
  const byType = new Map<MachineType, MachineRecord[]>()
  for (const m of machines) {
    const list = byType.get(m.type) ?? []
    list.push(m)
    byType.set(m.type, list)
  }

  return MACHINE_TYPES.filter((t) => byType.has(t.value)).map((t) => ({
    type: t.value,
    label: t.label,
    accent: t.accent,
    items: (byType.get(t.value) ?? []).sort((a, b) => a.sort - b.sort || a.number - b.number),
  }))
}

export function formatHours(value: number): string {
  if (Number.isInteger(value)) return String(value)
  return value.toFixed(1).replace(/\.0$/, '')
}
